import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, Play, Volume2, VolumeX, Heart, Instagram, Camera, Gift, Cake, GraduationCap, Baby, Church, Building2, ShieldCheck, Sparkle } from "lucide-react";
import { api, API_BASE, brl, productImage } from "../lib/api";
import { useAuth } from "../lib/store";

const OCCASIONS = [
  { icon: Gift, label: "Lembrancinhas" },
  { icon: Cake, label: "Aniversários" },
  { icon: Baby, label: "Chá de bebê" },
  { icon: GraduationCap, label: "Formaturas" },
  { icon: Heart, label: "Casamentos" },
  { icon: Church, label: "Eventos" },
  { icon: Building2, label: "Empresas" },
];

const HERO_STORIES = [
  {
    tag: "Novidade · Eterniza Conecta",
    title: "Uma foto por fora.",
    script: "Uma história por dentro.",
    subtitle: "Chaveiro com tag NFC que abre vídeos, áudios e mensagens ao aproximar o celular.",
    cta: { to: "/produto/chaveiro-nfc", label: "Conhecer o NFC" },
    bg: "https://images.pexels.com/photos/1670977/pexels-photo-1670977.jpeg?auto=compress&cs=tinysrgb&w=1600",
    accent: "#D4AF37",
  },
  {
    tag: "Presentes personalizados",
    title: "Momentos que você vê.",
    script: "Histórias que você revive.",
    subtitle: "Imãs, botons, chaveiros e polaróides feitos com carinho para durar.",
    cta: { to: "/catalogo", label: "Ver catálogo" },
    bg: "https://images.pexels.com/photos/1174746/pexels-photo-1174746.jpeg?auto=compress&cs=tinysrgb&w=1600",
    accent: "#E4C570",
  },
  {
    tag: "Datas especiais",
    title: "Cada ocasião merece",
    script: "uma lembrança única.",
    subtitle: "Cadastre suas datas e receba sugestões antes que sua história aconteça.",
    cta: { to: "/entrar", label: "Cadastrar datas" },
    bg: "https://images.pexels.com/photos/1128678/pexels-photo-1128678.jpeg?auto=compress&cs=tinysrgb&w=1600",
    accent: "#F5C1C1",
  },
];

function useIntersect(ref, threshold = 0.15) {
  const [visible, setV] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setV(e.isIntersecting), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);
  return visible;
}

function HeroCarousel() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % HERO_STORIES.length), 6500);
    return () => clearInterval(t);
  }, []);
  const s = HERO_STORIES[i];
  return (
    <section className="relative overflow-hidden h-[86vh] min-h-[560px] max-h-[820px] text-white">
      <AnimatePresence mode="wait">
        <motion.div key={i}
          initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }} transition={{ duration: 1.2 }}
          className="absolute inset-0"
        >
          <img src={s.bg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/95 via-[#0D1B2A]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A] via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 h-full flex flex-col justify-end pb-20">
        <AnimatePresence mode="wait">
          <motion.div key={i}
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <div className="eyebrow text-[#D4AF37]">{s.tag}</div>
            <h1 className="mt-4 font-display text-[42px] sm:text-[58px] lg:text-[74px] leading-[1.02] tracking-tight">
              {s.title}<br />
              <span className="font-script text-[54px] sm:text-[68px] lg:text-[88px]" style={{ color: s.accent }}>
                {s.script}
              </span>
            </h1>
            <p className="mt-5 text-white/85 text-lg max-w-xl">{s.subtitle}</p>
            <div className="mt-8 flex gap-3">
              <Link to={s.cta.to} data-testid="hero-primary" className="btn-gold inline-flex items-center gap-2">
                {s.cta.label} <ArrowRight size={16} />
              </Link>
              <Link to="/catalogo" className="btn-outline-navy" style={{ borderColor: "#fff", color: "#fff" }}>
                Todos os produtos
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex gap-2">
          {HERO_STORIES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              aria-label={`slide ${idx + 1}`}
              className={`h-1 rounded-full transition-all ${idx === i ? "w-10 bg-[#D4AF37]" : "w-4 bg-white/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductRow({ title, subtitle, products, accent = "#D4AF37" }) {
  const scroller = useRef(null);
  const scroll = (dir) => {
    if (!scroller.current) return;
    scroller.current.scrollBy({ left: dir * 380, behavior: "smooth" });
  };
  return (
    <section className="relative py-10 lg:py-14">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="eyebrow" style={{ color: accent }}>{title}</div>
          {subtitle && <h2 className="mt-2 font-display text-3xl md:text-4xl text-[#0D1B2A]">{subtitle}</h2>}
        </div>
        <div className="hidden md:flex gap-2">
          <button data-testid={`row-prev-${title}`} onClick={() => scroll(-1)} className="w-10 h-10 rounded-full bg-white border border-[#0D1B2A]/10 hover:border-[#D4AF37]">←</button>
          <button data-testid={`row-next-${title}`} onClick={() => scroll(1)} className="w-10 h-10 rounded-full bg-white border border-[#0D1B2A]/10 hover:border-[#D4AF37]">→</button>
        </div>
      </div>
      <div ref={scroller} className="mx-auto max-w-7xl px-6 lg:px-8 overflow-x-auto scroll-smooth" style={{ scrollbarWidth: "none" }}>
        <div className="flex gap-5 min-w-max pb-2">
          {products.map((p) => (
            <Link key={p.id} to={`/produto/${p.slug}`} data-testid={`row-product-${p.slug}`}
              className="group w-[260px] shrink-0 bg-white rounded-2xl overflow-hidden border border-[#0D1B2A]/5 lift">
              <div className="aspect-[4/5] overflow-hidden bg-[#F7F2E7] relative">
                <img src={productImage(p)} alt={p.name} className="product-img w-full h-full object-cover" />
                {p.highlight && (
                  <span className="absolute top-3 left-3 bg-[#D4AF37] text-[#0D1B2A] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
                    {p.highlight}
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <div className="text-white">
                    <div className="text-[11px] tracking-widest uppercase text-[#D4AF37]">Ver detalhes</div>
                    <div className="font-display text-lg">{p.tagline || "Personalize com sua foto"}</div>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="font-display text-[16px] text-[#0D1B2A] leading-tight">{p.name}</div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-xs text-[#6B7280] uppercase tracking-wider">{p.category}</span>
                  <span className="font-display text-lg text-[#0D1B2A]">{brl(p.price)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function InstagramBanner() {
  const { user } = useAuth();
  const [granted, setGranted] = useState(false);
  const [busy, setBusy] = useState(false);

  const follow = async () => {
    window.open("https://instagram.com/eternizaimas", "_blank", "noopener");
    if (!user) return; // não logado: informa que precisa cadastrar
    setBusy(true);
    try {
      const { data } = await api.post("/customers/instagram-follow", {});
      setGranted(true);
      // sonner not imported here; visual state suffices
    } finally { setBusy(false); }
  };

  return (
    <section className="relative dark-hero grain overflow-hidden py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center relative">
        <div>
          <div className="eyebrow text-[#D4AF37]">Cupom exclusivo</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl text-white">
            Siga <span className="text-[#D4AF37]">@eternizaimas</span> e ganhe
            <span className="block font-script text-[#D4AF37] text-6xl md:text-7xl mt-1">10% off</span>
          </h2>
          <p className="mt-4 text-white/80 max-w-lg">
            Ao clicar em "Segui!", você abrirá nosso perfil no Instagram e receberá automaticamente o cupom <b className="text-[#D4AF37]">EUSIGO10</b> para usar em qualquer produto.
          </p>
          <div className="mt-6 flex gap-3">
            <button data-testid="follow-instagram" onClick={follow} disabled={busy} className="btn-gold inline-flex items-center gap-2">
              <Instagram size={18} /> {granted ? "Cupom liberado!" : "Segui! Liberar cupom"}
            </button>
            {!user && (
              <Link to="/entrar" data-testid="ig-need-login" className="btn-outline-navy" style={{ borderColor: "#fff", color: "#fff" }}>
                Criar conta para receber
              </Link>
            )}
          </div>
          {granted && (
            <div className="mt-6 bg-[#D4AF37] text-[#0D1B2A] rounded-2xl p-5 inline-block">
              <div className="text-[11px] tracking-widest">SEU CUPOM</div>
              <div className="font-display text-3xl tracking-widest">EUSIGO10</div>
            </div>
          )}
        </div>
        <div className="relative">
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {[
              "https://images.pexels.com/photos/17951094/pexels-photo-17951094.jpeg?auto=compress&cs=tinysrgb&w=400",
              "https://images.pexels.com/photos/15863785/pexels-photo-15863785.jpeg?auto=compress&cs=tinysrgb&w=400",
              "https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=400",
              "https://images.pexels.com/photos/1128678/pexels-photo-1128678.jpeg?auto=compress&cs=tinysrgb&w=400",
              "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400",
              "https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=400",
            ].map((s, i) => (
              <div key={i} className={`aspect-square rounded-xl overflow-hidden border-2 border-white/20 ${i === 2 ? "ring-2 ring-[#D4AF37]" : ""}`}>
                <img src={s} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function OccasionsBand() {
  return (
    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <div className="eyebrow">Para cada momento</div>
        <h2 className="mt-3 font-display text-4xl md:text-5xl text-[#0D1B2A]">
          Perfeito para <em className="font-script text-[#D4AF37] not-italic">todas as ocasiões</em>
        </h2>
      </div>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {OCCASIONS.map(({ icon: Icon, label }) => (
          <div key={label} className="bg-white rounded-2xl p-5 text-center border border-[#0D1B2A]/5 lift">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F7F2E7] flex items-center justify-center">
              <Icon size={20} className="text-[#0D1B2A]" />
            </div>
            <div className="mt-3 text-[13px] font-medium text-[#0D1B2A]">{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="bg-[#0D1B2A] text-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          [Sparkles, "Feito com amor", "Cada peça é única"],
          [ShieldCheck, "Pagamento seguro", "Mercado Pago"],
          [Camera, "Qualidade premium", "Impressão fotográfica"],
          [Heart, "Envio nacional", "Correios e transportadoras"],
        ].map(([I, t, s], i) => (
          <div key={i} className="flex items-start gap-3">
            <I size={20} className="text-[#D4AF37] mt-1" />
            <div>
              <div className="font-display text-lg">{t}</div>
              <div className="text-xs text-white/60">{s}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)); }, []);
  const cat = (k) => products.filter((p) => p.category === k);
  const highlighted = products.filter((p) => p.highlight);

  return (
    <div data-testid="home-root">
      <HeroCarousel />
      <TrustBar />

      <ProductRow title="Destaques da temporada" subtitle="O que mais eterniza histórias" products={highlighted.length ? highlighted : products.slice(0, 6)} />
      <ProductRow title="Chaveiros" subtitle="Sempre à mão, sempre com você" products={cat("chaveiros")} accent="#E4C570" />
      <ProductRow title="Imãs & Botons" subtitle="Memórias que você toca" products={[...cat("imas"), ...cat("botons")]} accent="#E66E6E" />

      <InstagramBanner />

      <ProductRow title="Fotos & serviços" subtitle="O clássico que nunca sai de moda" products={[...cat("fotos"), ...cat("adesivos"), ...cat("servicos")]} />

      <OccasionsBand />

      <section className="mx-auto max-w-7xl px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-[#F7F2E7] border border-[#D4AF37]/40 p-10 lg:p-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow">Datas especiais</div>
            <h3 className="mt-3 font-display text-3xl lg:text-4xl text-[#0D1B2A]">
              Cadastre-se e nunca esqueça uma <em className="font-script text-[#D4AF37] not-italic">data importante</em>
            </h3>
            <p className="mt-3 text-[#3A4A5F]">
              Aniversários, casamento, namoro ou qualquer data especial. Vamos avisar você antes com sugestões prontas.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/entrar" data-testid="cta-signup" className="btn-navy">Criar conta grátis</Link>
            <Link to="/agendar" className="btn-outline-navy">Agendar sessão</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
